import { useEffect, useState } from 'react';
import { apiGet, apiJson } from '../api/client';
import { mutateGallery } from '../hooks/galleryCache';
import { NewAssetModal } from './NewAssetModal';
import { VOICES } from '../constants/voices';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

const ENDPOINT = '/api/tools/speech';

interface CreateResult {
  id: string;
  [k: string]: unknown;
}

interface PresetItem {
  id: string;
  name: string;
}
interface PresetsResponse {
  presets: PresetItem[];
}

export function NewSpeechDialog({ open, onClose, onCreated }: Props) {
  const [text, setText] = useState('');
  const [voice, setVoice] = useState<string>('Kore');
  const [presetId, setPresetId] = useState<string>('');
  const [presets, setPresets] = useState<PresetItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    void (async () => {
      try {
        const res = await apiGet<PresetsResponse>('/api/voice-presets');
        setPresets(res.presets ?? []);
      } catch {
        setPresets([]);
      }
    })();
  }, [open]);

  const submit = async (): Promise<void> => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    setErr(null);
    try {
      const body: Record<string, unknown> = {
        text: t,
      };
      if (presetId) {
        body.presetId = presetId;
      } else {
        body.voice = voice;
      }
      const res = await apiJson<CreateResult>('POST', ENDPOINT, body);
      // Speech gallery is filtered by ?type; the default is `script`. Since
      // a newly-created clip is always `single` or `multi` (SCRIPT), it
      // belongs in the default tab. We prepend into the default endpoint
      // key so the SCRIPT tab shows it immediately.
      mutateGallery('/api/tools/speech?type=script', (prev) => {
        if (prev.some((it) => it.id === res.id)) return prev;
        return [res as unknown as { id: string }, ...prev];
      });
      onCreated?.();
      setText('');
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const footer = (
    <>
      <button type="button" onClick={onClose} disabled={busy}>
        Cancel
      </button>
      <button
        type="button"
        className="btn-primary"
        onClick={() => void submit()}
        disabled={busy || text.trim().length === 0}
      >
        {busy ? 'Generating…' : 'Generate'}
      </button>
    </>
  );

  return (
    <NewAssetModal
      open={open}
      onClose={onClose}
      busy={busy}
      title="New speech clip"
      footer={footer}
    >
      <div className="stack">
        <label className="stack">
          <span className="muted small">Text</span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            placeholder="The text the voice will speak…"
          />
        </label>
        <div className="row">
          <label className="stack" style={{ flex: 1 }}>
            <span className="muted small">Preset (optional)</span>
            <select
              value={presetId}
              onChange={(e) => setPresetId(e.target.value)}
            >
              <option value="">— none (use voice below) —</option>
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  🎛 {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="stack" style={{ flex: 1 }}>
            <span className="muted small">Voice</span>
            <select
              value={voice}
              onChange={(e) => setVoice(e.target.value)}
              disabled={presetId.length > 0}
            >
              {VOICES.map((v) => (
                <option key={v.name} value={v.name}>
                  {v.name} — {v.description}
                </option>
              ))}
            </select>
          </label>
        </div>
        {err ? <div className="status-missing small">{err}</div> : null}
      </div>
    </NewAssetModal>
  );
}
